import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Highlight } from 'react-instantsearch-dom';
import styled from 'styled-components';
import { ReactComponent as AlertIcon } from '../../../assets/images/svg/amber-alert-triangle.svg';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import BottomDrawer, { DrawerButtonsWrapper } from '../../Drawer/Drawer';
import Button from '../../Button/Button';
import SfhPrimaryButton from './SfhPrimaryButton';

const TitleText = styled.span`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: ${themeColors.colorTextPrimary};
`;

const SubtitleText = styled.span`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const AttentionBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 4px;
  background: ${themeColors.colorBackgroundSecondary};

  > svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const AttentionText = styled.p`
  margin: 0;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const SectionHeading = styled.h3`
  margin: 20px 0 8px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorSfhSuccessHeading};
`;

const DescriptionText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: ${themeColors.colorTextPrimary};

  .ais-Highlight-highlighted {
    background: ${themeColors.colorWarningnBackground};
    color: ${themeColors.colorTextPrimary};
    font-weight: 700;
  }
`;

const ActionsFooter = styled(DrawerButtonsWrapper)`
  && > button {
    height: 44px;
    font-family: ${variables.family.goldSilverHeaders}, sans-serif;
    font-weight: 600;
  }

  && > button.sfh-back,
  && > button.sfh-back:hover,
  && > button.sfh-back:focus,
  && > button.sfh-back:active {
    color: ${themeColors.colorSfhActionBlue};
    border-color: ${themeColors.colorSfhActionBlue};
  }
`;

const SellFromHomeProductDescriptionDrawer = ({
  isOpen,
  onClose,
  hit,
  showActions,
  onBack,
  onContinue,
}) => {
  const { t } = useTranslation();

  const footer = showActions ? (
    <ActionsFooter>
      <Button
        className="sfh-back"
        variant="primaryOutlined"
        textTransform="none"
        onClick={onBack}
        data-cy="button-sfh-description-back"
      >
        {t('sellWizard.sfhWizard.back')}
      </Button>
      <SfhPrimaryButton
        onClick={onContinue}
        data-cy="button-sfh-description-continue"
      >
        {t('sellWizard.sfhWizard.continue')}
      </SfhPrimaryButton>
    </ActionsFooter>
  ) : undefined;

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={hit?.Caption ? <TitleText>{hit.Caption}</TitleText> : ''}
      subtitle={
        hit?.WeightCaption ? (
          <SubtitleText>{hit.WeightCaption}</SubtitleText>
        ) : undefined
      }
      footer={footer}
    >
      <AttentionBox>
        <AlertIcon />
        <AttentionText>
          {t('sellWizard.sfhWizard.productDescription.attentionNote')}
        </AttentionText>
      </AttentionBox>
      <SectionHeading>
        {t('sellWizard.sfhWizard.productDescription.heading')}
      </SectionHeading>
      {hit && (
        <DescriptionText>
          <Highlight attribute="Description" hit={hit} />
        </DescriptionText>
      )}
    </BottomDrawer>
  );
};

SellFromHomeProductDescriptionDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  hit: PropTypes.shape({
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
    Description: PropTypes.string,
  }),
  showActions: PropTypes.bool,
  onBack: PropTypes.func,
  onContinue: PropTypes.func,
};

export default SellFromHomeProductDescriptionDrawer;
